/**
 * Compute a selector and a human-readable label for an element picked in
 * feedback element-pick mode.
 *
 * ── Why this is not just "walk up four levels"
 *
 * The previous version capped the chain at 4 ancestors and only stopped early on
 * an id. On a page whose chain has no ids it hit the cap UNANCHORED, producing a
 * relative selector that can match anywhere in the document. Measured on real
 * tickets: #166 stored `div > div:nth-of-type(1) > div > p:nth-of-type(2)`,
 * which matches FIVE elements on the live page, and a BEN-141 release-check
 * ticket stored a mobile-web chain matching SEVEN.
 *
 * That mattered little while nothing rendered the selector. It matters now that
 * the QA ticket body carries it, because an ambiguous selector shown to a reader
 * implies precision it does not have.
 *
 * So instead of a fixed depth: build upward and stop at the FIRST depth that
 * identifies exactly one element, preferring stable anchors (id, then
 * aria-label) over position. `selectorMatchCount` then lets the caller state
 * plainly when the result is still not unique, rather than leaving the reader to
 * assume it is.
 */

/**
 * Hard bound on the positional walk.
 *
 * 16, not a guess: measured on the deployed mobile-web dashboard, the depth at
 * which a dashboard tile stops being ambiguous is 12 — react-native-web nests
 * wrappers deeply, so the distinguishing ancestor sits further up than on a
 * semantic page. At 10 the "Savings & Goals" tile still matched 7 elements; at
 * 12 it matches 1. 16 leaves headroom without being unbounded.
 *
 * Cost is bounded and small: the loop returns at the FIRST unique depth, so a
 * semantic page still exits in 2-3 steps (a /privacy nav link resolves at depth
 * 2). Only the genuinely-anonymous case pays for the extra querySelectorAll calls.
 */
const MAX_DEPTH = 16;

/** Matches the backend's Feedback.element_label column width. */
const MAX_LABEL = 120;

/**
 * How many elements a selector currently matches. 1 = unambiguous; >1 means the
 * ticket must say so; 0 means it no longer resolves (the page has changed).
 */
export function selectorMatchCount(selector: string): number {
  if (!selector) return 0;
  try {
    return document.querySelectorAll(selector).length;
  } catch {
    // A malformed selector must never break the widget people use to tell us
    // things are broken.
    return 0;
  }
}

function isUnique(selector: string): boolean {
  return selectorMatchCount(selector) === 1;
}

/**
 * `tag[aria-label="…"]` when the element carries one.
 *
 * On react-native-web this is the single most valuable anchor available: 98% of
 * the tree is anonymous <div> and almost nothing has an id, but 27 mobile source
 * files already set `accessibilityLabel`, which becomes `aria-label` on web. It
 * is both stable across re-renders and readable by a human.
 */
function ariaSelectorFor(el: Element): string | null {
  const aria = el.getAttribute("aria-label");
  if (!aria?.trim()) return null;
  // Escape for a quoted attribute value — NOT CSS.escape, which is for identifiers.
  const value = aria.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `${el.tagName.toLowerCase()}[aria-label="${value}"]`;
}

/** A stable anchor for an ancestor: its id, else its aria-label. */
function anchorFor(el: Element): string | null {
  if (el.id) return `#${escapeId(el.id)}`;
  return ariaSelectorFor(el);
}

export function cssSelectorFor(el: Element | null): string {
  if (!el || el.nodeType !== 1) return "";

  // Decisive anchors on the element itself, cheapest first.
  if (el.id) {
    const byId = `#${escapeId(el.id)}`;
    if (isUnique(byId)) return byId;
  }
  const byAria = ariaSelectorFor(el);
  if (byAria && isUnique(byAria)) return byAria;

  const parts: string[] = [];
  let node: Element | null = el;
  let depth = 0;

  while (node && node.nodeType === 1 && node !== document.body && depth < MAX_DEPTH) {
    let part = node.tagName.toLowerCase();
    const parent: Element | null = node.parentElement;
    if (parent) {
      const sameTag = Array.from(parent.children).filter((c) => c.tagName === node!.tagName);
      if (sameTag.length > 1) {
        part += `:nth-of-type(${sameTag.indexOf(node) + 1})`;
      }
    }
    parts.unshift(part);

    // Stop at the shortest chain that resolves to exactly one element.
    const candidate = parts.join(" > ");
    if (isUnique(candidate)) return candidate;

    // An anchored ancestor ends the walk far more reliably than more depth.
    if (parent) {
      const anchor = anchorFor(parent);
      if (anchor) {
        const anchored = `${anchor} > ${candidate}`;
        if (isUnique(anchored)) return anchored;
      }
    }

    node = parent;
    depth += 1;
  }

  // Nothing disambiguated it. Return the best effort — the caller reports the
  // match count alongside, so this is never presented as if it were unique.
  return parts.join(" > ") || el.tagName.toLowerCase();
}

/**
 * A human-readable name for a picked element: what a reader would call it.
 *
 * The CSS selector is precise at best and ambiguous at worst, and the QA portal
 * never renders it — this label is what actually reaches a reader, via the
 * forwarded body's context footer. Prefers an explicit accessible name, then
 * image alt text, then the element's own visible text.
 */
export function elementLabelFor(el: Element | null): string {
  if (!el || el.nodeType !== 1) return "";

  const aria = el.getAttribute("aria-label") || el.getAttribute("title");
  if (aria?.trim()) return clip(aria);

  if (el.tagName === "IMG") {
    const alt = el.getAttribute("alt");
    if (alt?.trim()) return clip(`image: ${alt}`);
  }

  // innerText reflects what is actually rendered (collapsed, hidden text
  // excluded); textContent is the fallback for SVG and other non-HTML elements.
  const text = (el as HTMLElement).innerText || el.textContent || "";
  const label = clip(text);
  if (label) return label;

  // Nothing readable — an icon-only or purely decorative node. Naming the tag
  // still beats an empty string for someone reading the ticket.
  return `<${el.tagName.toLowerCase()}>`;
}

/**
 * `CSS.escape` with a fallback: the global `CSS` object exists in browsers but
 * not in every DOM-ish environment (jsdom, test renderers), and an
 * undefined-global crash inside the feedback widget would take out the one
 * mechanism people use to report crashes.
 *
 * Kept byte-identical to mobile/src/components/feedback/selector.ts — see the
 * header comment there for why that file is a deliberate copy.
 */
function escapeId(id: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(id);
  return id.replace(/([^\w-])/g, "\\$1");
}

/** Collapse whitespace and cap length, so a whole card's text can't land in the label. */
function clip(value: string): string {
  const flat = value.replace(/\s+/g, " ").trim();
  return flat.length <= MAX_LABEL ? flat : `${flat.slice(0, MAX_LABEL - 1)}…`;
}
